// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authAPI, teamMemberAPI } from "../services/api";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getTimezoneCurrency } from "../utils/currencyMapper";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// -------------------------------
// Normalize user object
// -------------------------------
const normalizeUser = (user) => {
  if (!user) return null;

  const normalized = {
    ...user,
    teamMembership: user.team_membership
      ? {
        ...user.team_membership,
        role: user.team_membership.role || null,
      }
      : user.teamMembership || null,
    avatar: user.avatar || user.avatar_url || null,
  };

  normalized.hasPermission = function (perm) {
    if (!this.teamMembership?.role) return false;
    if (this.teamMembership.role.name?.toLowerCase().trim() === "system administrator") return true;

    return (this.teamMembership.role.permissions || []).some(
      (p) => p.name === perm
    );
  };

  return normalized;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currency, setCurrency] = useState({ code: 'USD', symbol: '$' });

  const navigate = useNavigate();

  // -------------------------------
  // Load user if access token exists
  // -------------------------------
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchUserData();
  }, []);

  // -------------------------------
  // Fetch logged-in user data
  // -------------------------------
  const fetchUserData = useCallback(async () => {
    try {
      const res = await authAPI.me();
      const normalized = normalizeUser(res.data.user);

      setUser(normalized);
      setCompany(res.data.company);
      setTrialInfo(res.data.trial_info);
      const tz = res.data.company?.timezone || 'UTC';
      setCurrency(getTimezoneCurrency(tz));

      // --- Subscription Control ---
      const trial = res.data.trial_info;
      const comp = res.data.company;
      const isAdmin =
        normalized?.teamMembership?.role?.name?.toLowerCase() === "system administrator";

      const isClient = !!normalized.client_id;

      if (!trial.is_trial_active && !comp.is_subscribed) {
        if (isAdmin) {
          navigate("/packages");
        } else {
          logout();
          navigate("/login", {
            state: { message: "Subscription expired. Contact your admin." },
          });
        }
      }
    } catch (err) {
      console.error("Failed to load user", err);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  // -------------------------------
  // Login
  // -------------------------------
  const login = async (email, password, licenseKey = "") => {
    try {
      const res = await authAPI.login({
        email,
        password,
        license_key: licenseKey,
      });

      const { access_token } = res.data;

      localStorage.setItem("access_token", access_token);
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${access_token}`;

      const normalizedUser = normalizeUser(res.data.user);

      setUser(normalizedUser);
      setCompany(res.data.company);
      setTrialInfo(res.data.trial_info);

      // Subscription gate
      const { is_trial_active } = res.data.trial_info;
      const { is_subscribed } = res.data.company;

      if (!is_trial_active && !is_subscribed) {
        const isAdmin =
          normalizedUser.teamMembership?.role?.name
            ?.toLowerCase()
            .trim() === "system administrator";

        if (!isAdmin) {
          logout();

          return {
            success: false,
            error: "Subscription expired for non-admin.",
          };
        }

        navigate("/packages");
      } else {
        const roleName =
          normalizedUser.teamMembership?.role?.name
            ?.toLowerCase()
            .trim();

        const isClient = !!normalizedUser.client_id;

        // ✅ Client
        if (isClient) {
          navigate("/projects");
        }

        // ✅ System Administrator
        else if (roleName === "system administrator") {
          navigate("/dashboard");
        }

        // ✅ Other users
        else {
          navigate("/board");
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  };

  // -------------------------------
  // Register new company
  // -------------------------------
  const register = async (data) => {
    try {
      const res = await authAPI.register(data);
      return { success: true, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.errors || "Registration failed",
      };
    }
  };

  // -------------------------------
  // Update user (🔥 NEW – for profile update without reload)
  // -------------------------------
  const updateUser = (updatedData) => {
    setUser((prev) => {
      if (!prev) return null;

      const merged = normalizeUser({
        ...prev,
        ...updatedData,
      });

      return merged;
    });
  };

  // -------------------------------
  // Team members
  // -------------------------------
  const fetchTeamMembers = async () => {
    try {
      const res = await teamMemberAPI.getAll();
      setTeamMembers(res.data);
    } catch {
      setTeamMembers([]);
    }
  };

  // -------------------------------
  // Logout
  // -------------------------------
  const logout = () => {
    localStorage.removeItem("access_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setCompany(null);
    setTrialInfo(null);
  };

  const value = {
    user,
    company,
    trialInfo,
    currency,
    loading,
    teamMembers,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    refreshTeamMembers: fetchTeamMembers,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};